import { useCallback, useEffect, useState } from "react";
import { Autocomplete, Checkbox, FormControlLabel, Modal, Box, Typography, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { DOMAIN_API_HOST } from "../../../configs/main";
import "./GisParser.scss";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 520,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "12px",
  boxShadow: 24,
  p: 4,
};

const STATUS_LABEL = {
  queued: "В очереди",
  running: "Выполняется",
  done: "Отправлено на почту",
  error: "Ошибка",
};
const MAX_CLIENTS = 100;
const FILTER_CHIPS = [
  { key: "only_metrika", label: "Метрика" },
  { key: "only_website", label: "Сайт" },
  { key: "only_phone", label: "Номер" },
];

function formatTime(ts) {
  if (!ts) return "—";
  const date = new Date(ts * 1000);
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: "Bearer " + localStorage.getItem("admin_token"),
  };
}

function errorDetail(data, fallback) {
  if (!data) return fallback;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
  return fallback;
}

function GisParser() {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [count, setCount] = useState(10);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState(null);
  const [onlyMetrika, setOnlyMetrika] = useState(false);
  const [onlyWebsite, setOnlyWebsite] = useState(false);
  const [onlyPhone, setOnlyPhone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async () => {
    const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/gis/status`, {
      headers: authHeaders(),
    });
    if (!res.ok) return;
    const data = await res.json();
    setStatus(data);
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/gis/cities`, {
        headers: authHeaders(),
      });
      if (!res.ok || cancelled) return;
      const data = await res.json();
      if (!Array.isArray(data) || cancelled) return;
      setCities(data);
      setCity((current) => current || data.find((item) => item.slug === "moscow") || data[0] || null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const hasActive = status?.jobs?.some((job) => job.status === "queued" || job.status === "running");
    if (!hasActive) return undefined;
    const id = window.setInterval(loadStatus, 8000);
    return () => window.clearInterval(id);
  }, [status, loadStatus]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    const parsedCount = Number(count);
    if (!city?.slug) {
      enqueueSnackbar("Выберите город", {
        variant: "error",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }
    if (!trimmed) {
      enqueueSnackbar("Введите поисковый запрос", {
        variant: "error",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }
    if (!parsedCount || parsedCount < 1 || parsedCount > MAX_CLIENTS) {
      enqueueSnackbar(`Количество клиентов: от 1 до ${MAX_CLIENTS}`, {
        variant: "error",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/gis/request`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          query: trimmed,
          city: city.slug,
          count: parsedCount,
          only_metrika: onlyMetrika,
          only_website: onlyWebsite,
          only_phone: onlyPhone,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        enqueueSnackbar(errorDetail(data, "Не удалось поставить запрос в очередь"), {
          variant: "error",
          autoHideDuration: 4000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        return;
      }
      enqueueSnackbar(
        data.position > 1
          ? `Запрос в очереди, позиция ${data.position}. CSV придёт на почту.`
          : "Запрос принят. CSV придёт на почту, когда сбор завершится.",
        {
          variant: "success",
          autoHideDuration: 4000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        }
      );
      setQuery("");
      setCount(10);
      setOpen(false);
      loadStatus();
    } catch (error) {
      enqueueSnackbar(error.message || "Ошибка запроса", {
        variant: "error",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="GisParser">
      <div className="GisParser-header">
        <div className="GisParser-headerText">
          <h1>Парсер</h1>
          <p>
            Сбор контактов организаций по поисковому запросу. Результат придёт CSV-файлом на почту.
            Одновременно выполняется только один запрос.
          </p>
        </div>
        <button type="button" className="GisParser-requestBtn" onClick={() => setOpen(true)}>
          Новый запрос
        </button>
      </div>

      <div className="GisParser-meta">
        <div className="GisParser-card">
          <span className="GisParser-cardLabel">Лимит</span>
          <span className="GisParser-cardValue">
            {status?.limit_applies
              ? `${status.remaining} из ${status.limit} в этом часе`
              : "Без ограничения"}
          </span>
        </div>
        <div className="GisParser-card">
          <span className="GisParser-cardLabel">Очередь</span>
          <span className="GisParser-cardValue">
            {status ? `${status.queue_length} в работе` : "—"}
          </span>
        </div>
      </div>

      <div className="GisParser-tableWrap">
        <h2>Мои запросы</h2>
        {!status?.jobs?.length ? (
          <p className="GisParser-empty">Пока нет запросов</p>
        ) : (
          <table className="GisParser-table">
            <thead>
              <tr>
                <th>Запрос</th>
                <th>Город</th>
                <th>Кол-во</th>
                <th>Статус</th>
                <th>Найдено</th>
                <th>Время</th>
              </tr>
            </thead>
            <tbody>
              {status.jobs.map((job) => (
                <tr key={job.id}>
                  <td>
                    <div>{job.query}</div>
                    {(job.only_metrika || job.only_website || job.only_phone) && (
                      <div className="GisParser-chips">
                        {FILTER_CHIPS.filter((chip) => job[chip.key]).map((chip) => (
                          <span key={chip.key} className="GisParser-chip">
                            {chip.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>{job.city_name || "—"}</td>
                  <td>{job.count}</td>
                  <td>
                    <span className={`GisParser-status GisParser-status--${job.status}`}>
                      {STATUS_LABEL[job.status] || job.status}
                      {job.status === "queued" && job.position ? ` · ${job.position}` : ""}
                    </span>
                    {job.error ? <div className="GisParser-error">{job.error}</div> : null}
                  </td>
                  <td>{job.status === "done" ? job.found : "—"}</td>
                  <td>{formatTime(job.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={open} onClose={() => !submitting && setOpen(false)}>
        <Box sx={modalStyle} component="form" onSubmit={handleSubmit}>
          <Typography variant="h6" component="h2">
            Запросить клиентов из 2GIS
          </Typography>
          <div className="GisParser-guide">
            Выберите город и введите тип организации. Пример: город «Москва», запрос «Автомойка».
          </div>
          <Typography sx={{ mb: 3, color: "#42454C", opacity: 0.7, fontSize: 14 }}>
            CSV с результатами придёт на вашу почту. Если в очереди уже есть запросы, ваш будет выполнен следующим.
          </Typography>
          <Autocomplete
            disablePortal
            options={cities}
            value={city}
            onChange={(_, next) => setCity(next)}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.slug === value.slug}
            renderInput={(params) => <TextField {...params} label="Город" placeholder="Начните вводить название" />}
          />
          <TextField
            fullWidth
            label="Поисковый запрос"
            placeholder="Автомойка"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            type="number"
            label="Количество клиентов"
            value={count}
            onChange={(e) => {
              const next = e.target.value;
              if (next === "") {
                setCount("");
                return;
              }
              const parsed = Number(next);
              if (!Number.isFinite(parsed)) return;
              setCount(Math.min(MAX_CLIENTS, Math.max(1, Math.floor(parsed))));
            }}
            helperText={`Не больше ${MAX_CLIENTS} за один запрос`}
            inputProps={{ min: 1, max: MAX_CLIENTS }}
            sx={{ mt: 2 }}
          />
          <div className="GisParser-filters">
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyMetrika}
                  onChange={(e) => setOnlyMetrika(e.target.checked)}
                  size="small"
                />
              }
              label="Только клиенты с Яндекс Метрикой"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyWebsite}
                  onChange={(e) => setOnlyWebsite(e.target.checked)}
                  size="small"
                />
              }
              label="Только клиенты с сайтом"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyPhone}
                  onChange={(e) => setOnlyPhone(e.target.checked)}
                  size="small"
                />
              }
              label="Только клиенты с номером"
            />
          </div>
          <div className="GisParser-modalActions">
            <button type="button" className="GisParser-cancelBtn" onClick={() => setOpen(false)} disabled={submitting}>
              Отмена
            </button>
            <button type="submit" className="GisParser-submitBtn" disabled={submitting}>
              {submitting ? "Отправка..." : "Запросить"}
            </button>
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default GisParser;
